import { UserSwitchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Form, Popconfirm, Space, Table, TimePicker, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UTCNoShiftRangePicker } from 'src/components/v2/DatePicker';
import { InputNumber } from 'src/components/v2/InputNumber';
import { EmployeeSelectV2 } from 'src/components/v2/Select';
import useCalcTimeBusiness from 'src/hooks/useCalcTimeBusiness';
import type { SelectOptionType } from 'src/types/elements.type';
import type { BusinessTripTicketDetailConfirmed } from 'src/types/hr_ticket/businessTripTicket.type';
import { showNotification } from 'src/utils/notification';
import { formatDate, formatTimeString, totalOfRangeDate } from 'src/utils/utils';
import { v4 as uuid } from 'uuid';

const { Text } = Typography;

const columns = function (
  isConfirm: boolean,
  formState: FormStateType,
  setFormState: Dispatch<SetStateAction<FormStateType>>,
  callbackAddNewRow: (record: Partial<BusinessTripTicketDetailConfirmed>) => void,
  callbackSave: () => void,
  callbackEdit: (record: Partial<BusinessTripTicketDetailConfirmed>) => void,
  callbackCancel: (record: Partial<BusinessTripTicketDetailConfirmed>) => void,
  callbackDelete: (record: Partial<BusinessTripTicketDetailConfirmed>) => void,
  editingKey: string,
  t: TFunction<'translation', undefined, 'translation'>,
  // disabledDate: RangePickerProps['disabledDate'],
  disableReplaceBtn: (employeeId: number | undefined, fromDate: Date | undefined, toDate: Date | undefined) => boolean,
  calcTotalTime: (
    startTimeInput: string | undefined,
    endTimeInput: string | undefined,
    employeeId: number | undefined
  ) => number
): ColumnsType<Partial<BusinessTripTicketDetailConfirmed>> {
  const columnTitle: ColumnsType<Partial<BusinessTripTicketDetailConfirmed>> = [
    {
      title: t('Người đăng ký'),
      dataIndex: 'employee',
      width: '20%',
      key: 'employee',
      render: (_, record) => {
        if (formState.id === record.id && typeof record.id === 'string') {
          return (
            <Form.Item name='employee' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
              <Flex vertical gap={6}>
                <EmployeeSelectV2
                  style={{ width: 280 }}
                  exclusionEmployee={formState.replaceForId}
                  value={formState.employee ? JSON.stringify(formState.employee) : undefined}
                  onChange={(value: number, option: SelectOptionType) => {
                    const data = option.data;
                    setFormState((prev) => ({ ...prev, employee: data, employeeId: value }));
                  }}
                />
                <Text>
                  {record.replaceFor && <Text type='secondary'>(Thay thế: {record.replaceFor?.fullName})</Text>}
                </Text>
              </Flex>
            </Form.Item>
          );
        }
        return (
          <Flex vertical gap={4}>
            <Text>
              {record.employee?.code} - {record.employee?.fullName}
            </Text>
            <Text>{record.replaceFor && <Text type='secondary'>(Thay thế: {record.replaceFor?.fullName})</Text>}</Text>
          </Flex>
        );
      }
    },
    {
      title: t('Ngày công tác'),
      dataIndex: 'date',
      width: '18%',
      align: 'center',
      key: 'date',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='date' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
              <Flex vertical gap={5} align='center'>
                <UTCNoShiftRangePicker
                  suffixIcon={false}
                  allowClear={false}
                  disabledDate={(current) => {
                    const from = dayjs(formState.actualFromDate).startOf('day');
                    const to = dayjs(formState.actualToDate).endOf('day');
                    return current.isBefore(from) || current.isAfter(to);
                  }}
                  style={{
                    borderInline: 0,
                    borderBlockStart: 0,
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    paddingInline: 0,
                    boxShadow: 'none',
                    width: 200
                  }}
                  onChangeUTC={(dates) => {
                    const [fromDate, toDate] = dates as Date[];
                    setFormState((prev) => ({ ...prev, fromDate, toDate }));
                  }}
                />

                <Text strong>Tổng: {totalOfRangeDate(formState?.fromDate, formState?.toDate)} Ngày</Text>
              </Flex>
            </Form.Item>
          );
        }
        return (
          <Flex vertical gap={5} align='center'>
            <Text>{`${formatDate(record.fromDate)} -> ${formatDate(record.toDate)}`}</Text>
            <Text strong>Tổng: {totalOfRangeDate(record?.fromDate, record?.toDate)} Ngày</Text>
          </Flex>
        );
      }
    },
    {
      title: t('Thời gian'),
      dataIndex: 'time',
      width: '14%',
      align: 'center',
      key: 'time',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='time' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
              <Flex vertical gap={5} align='center'>
                <TimePicker.RangePicker
                  changeOnBlur
                  allowClear={false}
                  showSecond={false}
                  showNow={false}
                  minuteStep={30}
                  suffixIcon={null}
                  format='HH:mm'
                  size='small'
                  style={{
                    borderInline: 0,
                    borderBlockStart: 0,
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    paddingInline: 0,
                    boxShadow: 'none',
                    width: 140
                  }}
                  value={[
                    formState.startTime ? dayjs(formState.startTime, 'HH:mm') : null,
                    formState.endTime ? dayjs(formState.endTime, 'HH:mm') : null
                  ]}
                  onChange={(_, value) =>
                    setFormState((prev) => ({
                      ...prev,
                      startTime: value[0],
                      endTime: value[1]
                    }))
                  }
                  placeholder={['Từ giờ', 'Đến giờ']}
                />
                <Text strong>{`Tổng: ${calcTotalTime(
                  formState.startTime,
                  formState.endTime,
                  formState.employeeId
                )} Tiếng`}</Text>
              </Flex>
            </Form.Item>
          );
        }
        return (
          <Flex vertical gap={5} align='center'>
            <Text>{`${formatTimeString(record.startTime, true)} -> ${formatTimeString(record.endTime)}`}</Text>
            <Text strong>{`Tổng: ${calcTotalTime(record.startTime, record.endTime, record.employeeId)} Tiếng`}</Text>
          </Flex>
        );
      }
    },
    {
      title: <Text>{t('Số ngày lưu trú')}</Text>,
      dataIndex: 'numDaysOfStay',
      align: 'center',
      width: '10%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='numDaysOfStay' style={{ marginBottom: 0 }}>
              <InputNumber onChange={(value) => setFormState((prev) => ({ ...prev, numDaysOfStay: value }))} />
            </Form.Item>
          );
        }
        return <Text>{record.numDaysOfStay} Ngày</Text>;
      }
    },
    {
      title: <Text>{t('Số ngày công tác phí')}</Text>,
      dataIndex: 'numDaysOfBusinessTrip',
      align: 'center',
      width: '15%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item name='numDaysOfBusinessTrip' style={{ marginBottom: 0 }}>
              <InputNumber onChange={(value) => setFormState((prev) => ({ ...prev, numDaysOfBusinessTrip: value }))} />
            </Form.Item>
          );
        }
        return <Text>{record.numDaysOfBusinessTrip} Ngày</Text>;
      }
    },
    {
      title: 'Không xác nhận',
      key: 'isUnConfirm',
      align: 'center',
      width: '12%',
      render: (_, record) => {
        if (formState.id === record.id && !record.replaceForId) {
          return (
            <Form.Item name='isUnConfirm' style={{ marginBottom: 0 }}>
              <Checkbox
                onChange={() => setFormState((prev) => ({ ...prev, isUnConfirm: !prev.isUnConfirm }))}
                checked={formState.isUnConfirm}
              />
            </Form.Item>
          );
        }
        return (
          <Flex vertical align='center'>
            <Checkbox checked={record.isUnConfirm} disabled />
            {record.isUnConfirm && (
              <Button
                type='link'
                icon={<UserSwitchOutlined />}
                onClick={() => callbackAddNewRow(record)}
                disabled={disableReplaceBtn(record.employeeId, record.fromDate, record.toDate)}
              >
                Thay người khác
              </Button>
            )}
          </Flex>
        );
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center',
      width: '8%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Space size='small'>
              <Button type='link' size='small' onClick={callbackSave}>
                {t('action.save')}
              </Button>
              <Button type='link' size='small' danger onClick={() => callbackCancel(record)}>
                {t('action.cancel')}
              </Button>
            </Space>
          );
        }
        return (
          <Space size='small'>
            <Button type='link' disabled={editingKey !== ''} size='small' onClick={() => callbackEdit(record)}>
              {t('action.edit')}
            </Button>
            <Popconfirm
              title={t('notification.warning.delete')}
              disabled={typeof record.id === 'number'}
              onConfirm={() => callbackDelete(record)}
            >
              <Button type='link' size='small' disabled={typeof record.id === 'number'} danger>
                {t('action.delete')}
              </Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return isConfirm ? columnTitle.slice(0, columnTitle.length - 2) : columnTitle;
};

type FormStateType = BusinessTripTicketDetailConfirmed;

const initialFormState: FormStateType = {
  id: undefined,
  employee: undefined,
  employeeId: undefined,
  actualFromDate: undefined,
  actualToDate: undefined,
  fromDate: undefined,
  toDate: undefined,
  endTime: undefined,
  startTime: undefined,
  numDaysOfStay: 0,
  numDaysOfBusinessTrip: 0,
  isUnConfirm: false
};

type Props = {
  isConfirm: boolean;
  confirms: BusinessTripTicketDetailConfirmed[];
  handleSetFormState: (businessTripTicketDetails: BusinessTripTicketDetailConfirmed[]) => void;
};

function AddConfirmBusinessTripTicket({ isConfirm, confirms, handleSetFormState }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [addMode, setAddMode] = useState<'add' | 'edit'>('add');
  const [editingKey, setEditingKey] = useState('');
  const [formState, setFormState] = useState(initialFormState);
  const [businessTripTicketDetails, setBusinessTripTicketDetails] = useState<BusinessTripTicketDetailConfirmed[]>([]);
  const { calcTotalTime } = useCalcTimeBusiness();

  useEffect(() => {
    if (confirms) {
      setBusinessTripTicketDetails(confirms);
    }
  }, [confirms]);

  const disableReplaceBtn = (employeeId: number | undefined, fromDate: Date | undefined, toDate: Date | undefined) => {
    const index = businessTripTicketDetails?.findIndex(
      (x) => x.replaceForId === employeeId && x.fromDate === fromDate && x.toDate === toDate
    );
    return index === -1 ? false : true;
  };

  const handleAddNewRow = (record: Partial<BusinessTripTicketDetailConfirmed>) => {
    if (editingKey) {
      showNotification('Vui lòng hoàn thành dòng dữ liệu đang thao tác.', 'info');
    } else {
      const newRow: FormStateType = {
        ...initialFormState,
        refDetailId: record.id,
        actualFromDate: record.fromDate,
        actualToDate: record.toDate,
        toDate: record.toDate,
        fromDate: record.fromDate,
        startTime: record.startTime,
        endTime: record.endTime,
        replaceForId: record.employeeId,
        replaceFor: record.employee,
        numDaysOfStay: record.numDaysOfStay,
        numDaysOfBusinessTrip: record.numDaysOfBusinessTrip,
        id: uuid()
      };
      setBusinessTripTicketDetails((prev) => [...prev, newRow as BusinessTripTicketDetailConfirmed]);
      setEditingKey(String(newRow.id));
      setFormState(newRow);
      setAddMode('add');
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(() => {
        const replaceIndex = businessTripTicketDetails?.findIndex((x) => x.replaceForId === formState.employeeId);

        if (replaceIndex !== -1 && !formState.isUnConfirm) {
          showNotification('Không thể thay đổi xác nhận, vì nhân viên đã có người thay thế!', 'info');
        } else {
          let cloneDataSource = [...businessTripTicketDetails];
          const index = cloneDataSource?.findIndex((item) => item.id === formState.id);
          if (index === -1) return;

          cloneDataSource[index] = formState as BusinessTripTicketDetailConfirmed;
          setBusinessTripTicketDetails(cloneDataSource);
          setFormState(initialFormState);
          setEditingKey('');

          handleSetFormState(cloneDataSource);
        }
      })
      .catch(() => {});
  };

  const handleEdit = (record: Partial<BusinessTripTicketDetailConfirmed>) => {
    setFormState(record);
    setAddMode('edit');
    setEditingKey(String(record.id));
  };

  const handleCancel = (record: Partial<BusinessTripTicketDetailConfirmed>) => {
    if (addMode === 'add') {
      const newBusinessTripTicketDetails = businessTripTicketDetails.filter((x) => x.id !== record.id);
      setBusinessTripTicketDetails(newBusinessTripTicketDetails);
      setEditingKey('');
    } else {
      setFormState(initialFormState);
      setEditingKey('');
    }
  };

  const handleDelete = (record: Partial<BusinessTripTicketDetailConfirmed>) => {
    const newBusinessTripTicketDetails = businessTripTicketDetails.filter((x) => x.id !== record.id);
    setBusinessTripTicketDetails(newBusinessTripTicketDetails);
    handleSetFormState(newBusinessTripTicketDetails);
  };

  return (
    <Form
      form={form}
      fields={[
        {
          name: 'employee',
          value: formState.employeeId
        },
        {
          name: 'date',
          value: formState.fromDate && formState.toDate ? [dayjs(formState.fromDate), dayjs(formState.toDate)] : null
        },
        {
          name: 'time',
          value:
            formState.startTime && formState.endTime
              ? [dayjs(formState.startTime, 'HH:mm'), dayjs(formState.endTime, 'HH:mm')]
              : null
        },
        {
          name: 'numDaysOfStay',
          value: formState.numDaysOfStay
        },
        {
          name: 'numDaysOfBusinessTrip',
          value: formState.numDaysOfBusinessTrip
        }
      ]}
    >
      <Table
        className='business-trip-confirm-table'
        rowClassName={(record) => {
          return record.isUnConfirm !== undefined && !record.isUnConfirm
            ? 'confirmed'
            : record.isUnConfirm !== undefined && record.isUnConfirm
            ? 'un-confirmed'
            : '';
        }}
        columns={columns(
          isConfirm,
          formState,
          setFormState,
          handleAddNewRow,
          handleSave,
          handleEdit,
          handleCancel,
          handleDelete,
          editingKey,
          t,
          // disableDate,
          disableReplaceBtn,
          calcTotalTime
        )}
        rowKey={(record) => String(record.id)}
        dataSource={businessTripTicketDetails}
        pagination={false}
        bordered={false}
        scroll={{ x: 500 }}
        size='middle'
      />
    </Form>
  );
}

export default AddConfirmBusinessTripTicket;
